import React from 'react';
import { QuoteData } from '../../types';
import { formatCurrency, formatPercent, sumLiabilitiesPaidOff, sumAllMonthlyLiabilityPayments, calculateCurrentTotalMonthlyPayment, calculateProposedTotalMonthlyPayment } from '../../lib/calculations';
import { motion } from 'framer-motion';
interface LoanComparisonProps {
  data: QuoteData;
}
export function LoanComparison({
  data
}: LoanComparisonProps) {
  const liabilitiesPaidOff = sumLiabilitiesPaidOff(data.liabilities);
  const monthlyLiabilities = sumAllMonthlyLiabilityPayments(data.liabilities);
  const currentTotal = calculateCurrentTotalMonthlyPayment(data.currentPayment, data.liabilities);
  const proposedTotal = calculateProposedTotalMonthlyPayment(data.proposedPayment, data.liabilities);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 0.4,
    duration: 0.8,
    ease: 'easeOut'
  }} className="bg-charcoal-800 rounded-2xl border border-white/10 p-6 md:p-8" data-id="element-49">
      <div className="text-[10px] font-medium tracking-[0.2em] text-gray-500 mb-8 flex items-center gap-3" data-id="element-50">
        <span className="text-white/20" data-id="element-51">—</span> SIDE BY SIDE
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 md:gap-x-12 relative" data-id="element-52">
        {/* Divider for desktop */}
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2" data-id="element-53"></div>

        {/* Row 1: Eyebrow labels */}
        <div className="text-[10px] text-gray-500 mb-3 uppercase tracking-[0.15em]" data-id="element-54">
          Current Loan
        </div>
        <div className="text-[10px] text-gold mb-3 uppercase tracking-[0.15em] flex items-center justify-between" data-id="element-55">
          <span data-id="element-56">New Loan • {data.loanType}</span>
          <span className="border border-gold text-gold text-[9px] px-3 py-1 rounded-full tracking-[0.2em] uppercase" data-id="element-57">
            Proposed
          </span>
        </div>

        {/* Row 2: Big rate numbers */}
        <div className="font-serif font-light text-4xl md:text-5xl text-white mb-2" data-id="element-58">
          {formatPercent(data.currentRate)}
        </div>
        <div className="font-serif font-light text-4xl md:text-5xl text-gold mb-2" data-id="element-59">
          {formatPercent(data.proposedRate)}
        </div>

        {/* Row 3: Product / term subtext */}
        <div className="text-xs text-gray-500 font-light mb-6" data-id="element-60">
          {data.currentProduct}
        </div>
        <div className="text-xs text-gray-500 font-light mb-6" data-id="element-61">
          {data.proposedTerm} Year • new
        </div>

        {/* Row 4: Payment (with shared top border) */}
        <div className="flex justify-between items-center text-sm pt-6 pb-4 border-t border-white/10" data-id="element-62">
          <span className="text-gray-500 font-light" data-id="element-63">Payment (P&I)</span>
          <span className="text-white" data-id="element-64">
            {formatCurrency(data.currentPayment)}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm pt-6 pb-4 border-t border-white/10" data-id="element-65">
          <span className="text-gray-500 font-light" data-id="element-66">Payment (P&I)</span>
          <span className="text-gold" data-id="element-67">
            {formatCurrency(data.proposedPayment)}
          </span>
        </div>

        {/* Row 5: Balance */}
        <div className="flex justify-between items-center text-sm py-3" data-id="element-68">
          <span className="text-gray-500 font-light" data-id="element-69">Balance</span>
          <span className="text-white" data-id="element-70">
            {formatCurrency(data.currentBalance)}
          </span>
        </div>
        <div className="flex justify-between items-center text-sm py-3" data-id="element-71">
          <span className="text-gray-500 font-light" data-id="element-72">Balance</span>
          <span className="text-white" data-id="element-73">
            {formatCurrency(data.proposedLoanAmount)}
          </span>
        </div>

        {/* Row 6: Monthly Liabilities (current) / Liabilities Paid Off (proposed) */}
        {monthlyLiabilities > 0 ? <div className="flex justify-between items-center text-sm py-3" data-id="element-74">
            <span className="text-gray-500 font-light" data-id="element-75">
              Monthly Liabilities
            </span>
            <span className="text-white" data-id="element-76">
              {formatCurrency(monthlyLiabilities)}
            </span>
          </div> : <div aria-hidden="true" className="py-3" data-id="element-77"></div>}
        {liabilitiesPaidOff > 0 ? <div className="flex justify-between items-center text-sm py-3" data-id="element-78">
            <span className="text-gray-500 font-light" data-id="element-79">
              Liabilities Paid Off
            </span>
            <span className="text-gold" data-id="element-80">
              {formatCurrency(liabilitiesPaidOff)}
            </span>
          </div> : <div aria-hidden="true" className="py-3" data-id="element-81"></div>}

        {/* Row 7 (only when relevant): Total Monthly Payment summary */}
        {monthlyLiabilities > 0 && <>
            <div className="flex justify-between items-center pt-5 mt-1 border-t border-white/10" data-id="element-82">
              <span className="text-[10px] text-gray-500 uppercase tracking-[0.15em]" data-id="element-83">
                Total Monthly Payment
              </span>
              <span className="text-white font-medium" data-id="element-84">
                {formatCurrency(currentTotal)}
              </span>
            </div>
            <div className="flex justify-between items-center pt-5 mt-1 border-t border-white/10" data-id="element-85">
              <span className="text-[10px] text-gold uppercase tracking-[0.15em]" data-id="element-86">
                Total Monthly Payment
              </span>
              <span className="text-gold font-medium" data-id="element-87">
                {formatCurrency(proposedTotal)}
              </span>
            </div>
          </>}
      </div>
    </motion.div>;
}